import { getAuthenticatedUserData } from '@/services/masters/user/userApiCalls';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { commonActions } from '@/store/slices/commonSlice';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { CustomerConfig } from './CustomerConfig';
import { NonCustomerConfig } from './NonCustomerConfig';
import { useSearchCountry } from '@/hooks/search-hooks';
import { useProtectedAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';

export const Config = ({ children }: { children: React.ReactNode }) => {
  useSearchCountry();
  const { t } = useTranslation('common');
  const nav = useNavigate();
  const dispatch = useAppDispatch();
  const pathname = useLocation().pathname;
  const isPublicUrl = pathname.split('/')[1] === 'public';
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);

  const { signOut } = useProtectedAuth();

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const authUserData = useAppSelector((state) => state.user.authUserData);
  const user = authUserData?.data;
  const isCustomer = !!user.customer?.c_id;

  const noConfig = authStatus === 'unauthenticated' || !user.email;

  const apiCalledRef = useRef(false); // to avoid double api call in dev environment

  useEffect(() => {
    if (!isPublicUrl && authStatus === 'unauthenticated') {
      if (!pathname.includes('auth')) {
        dispatch(commonActions.setRedirectUrl(pathname));
      }
      nav('/auth/login');
    }
    if (authStatus === 'unauthenticated') {
      // clearing for auto signout
      signOut();
    }
  }, [authStatus, pathname]);

  useEffect(() => {
    if (user.name) {
      setIsLoggingIn(false);
    }

    if (authUserData.error) {
      setIsLoggingIn(false);
    }
  }, [user]);

  useEffect(() => {
    if (authStatus === 'authenticated' && !apiCalledRef.current) {
      apiCalledRef.current = true;
      dispatch(getAuthenticatedUserData());
      setIsLoggingIn(true);
    } else {
      apiCalledRef.current = false;
    }
  }, [authStatus]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const ConfigMode = isCustomer
    ? CustomerConfig
    : noConfig
      ? Fragment
      : NonCustomerConfig;

  return (
    <ConfigMode>
      {isLoggingIn && (
        <div className="fixed inset-0 z-[9999999999999999999] grid place-items-center bg-surface-0">
          <div className="flex flex-col items-center gap-5">
            <div>
              <i className="pi pi-spinner pi-spin text-7xl"></i>
            </div>
            <div className="animate-textshimmer bg-[linear-gradient(135deg,_#ffffff,_#5E5E5E,_#ffffff)] bg-[length:200%_100%] bg-clip-text text-2xl text-transparent">
              {t('signInMsg')}
            </div>
          </div>
        </div>
      )}
      {children}
    </ConfigMode>
  );
};
